import type { ErrorResponse } from "@shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type { AxiosError } from "axios";
import type { ListByStaffDailyAttendanceQuery, StaffDailyAttendanceItem } from "@/app/api/types";
import { dailyAttendanceService } from "../services/daily-attendance.service";
import type {
	CreateDailyAttendanceRequest,
	DailyAttendance,
	ListDailyAttendanceParams,
	UpdateDailyAttendanceRequest,
} from "../types";

// ─── Query Keys ─────────────────────────────────────────────────────
export const dailyAttendanceKeys = {
	all: ["daily-attendance"] as const,
	lists: () => [...dailyAttendanceKeys.all, "list"] as const,
	list: (params: ListDailyAttendanceParams) => {
		return [...dailyAttendanceKeys.lists(), params] as const;
	},
	byStaff: (params: ListByStaffDailyAttendanceQuery) => {
		return [...dailyAttendanceKeys.all, "by-staff", params] as const;
	},
	details: () => [...dailyAttendanceKeys.all, "detail"] as const,
	detail: (id: string) => [...dailyAttendanceKeys.details(), id] as const,
};

// ─── useDailyAttendanceList ─────────────────────────────────────────
export function useDailyAttendanceList(params: ListDailyAttendanceParams) {
	return useQuery({
		queryKey: dailyAttendanceKeys.list(params),
		queryFn: () => {
			return dailyAttendanceService.list(params);
		},
		enabled: !!params.organizationId,
	});
}

// ─── useDailyAttendanceByStaff ───────────────────────────────────────
/** Fetches staff with daily attendance for date range (by-staff endpoint, all pages) */
export function useDailyAttendanceByStaff(
	organizationId: string | null,
	fromDate: string | null,
	toDate: string | null
) {
	const datesValid = (() => {
		if (!(fromDate && toDate && organizationId)) {
			return false;
		}
		const from = new Date(fromDate);
		const to = new Date(toDate);
		return from <= to;
	})();

	return useQuery({
		queryKey: dailyAttendanceKeys.byStaff({
			organizationId: organizationId ?? "",
			fromDate: fromDate ?? undefined,
			toDate: toDate ?? undefined,
		}),
		queryFn: async (): Promise<StaffDailyAttendanceItem[]> => {
			if (!(organizationId && fromDate && toDate)) {
				return [];
			}
			const allItems: StaffDailyAttendanceItem[] = [];
			let page = 1;
			const limit = 100;
			let hasMore = true;

			while (hasMore) {
				const res = await dailyAttendanceService.listByStaff({
					organizationId,
					fromDate,
					toDate,
					page,
					limit,
				});
				const items = res.items ?? [];
				allItems.push(...items);
				const meta = res.meta;
				hasMore = meta ? page < meta.totalPages : items.length === limit;
				page += 1;
			}

			return allItems;
		},
		enabled: !!organizationId && datesValid,
		staleTime: 30_000,
	});
}

// ─── useDailyAttendanceForDateRange ──────────────────────────────────
/** Fetches daily attendance for date range in a single request (with pagination if needed) */
export function useDailyAttendanceForDateRange(
	organizationId: string | null,
	startDate: string | null,
	endDate: string | null
) {
	const datesValid = (() => {
		if (!startDate) {
			return false;
		}
		if (!endDate) {
			return false;
		}
		if (!organizationId) {
			return false;
		}
		const start = new Date(startDate);
		const end = new Date(endDate);
		return start <= end;
	})();

	return useQuery({
		queryKey: [...dailyAttendanceKeys.lists(), "range", organizationId, startDate, endDate],
		queryFn: async () => {
			if (!organizationId) {
				return [];
			}
			if (!startDate) {
				return [];
			}
			if (!endDate) {
				return [];
			}
			const allItems: DailyAttendance[] = [];
			let page = 1;
			const limit = 100;
			let hasMore = true;

			while (hasMore) {
				const res = await dailyAttendanceService.list({
					organizationId,
					startDate,
					endDate,
					page,
					limit,
				});
				const items = res.data?.items ?? [];
				allItems.push(...items);
				const meta = res.data?.meta;
				hasMore = meta ? page < meta.totalPages : items.length === limit;
				page += 1;
			}

			return allItems;
		},
		enabled: !!organizationId && datesValid,
		staleTime: 30_000,
	});
}

// ─── useDailyAttendance ──────────────────────────────────────────────
export function useDailyAttendance(id: string) {
	return useQuery({
		queryKey: dailyAttendanceKeys.detail(id),
		queryFn: () => {
			return dailyAttendanceService.getById(id);
		},
		enabled: !!id,
	});
}

// ─── useCreateDailyAttendance ───────────────────────────────────────
export function useCreateDailyAttendance() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateDailyAttendanceRequest) => {
			return dailyAttendanceService.create(data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: dailyAttendanceKeys.lists() });
			message.success("Kunlik davomat muvaffaqiyatli yaratildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Kunlik davomat saqlashda xatolik";
			message.error(msg);
		},
	});
}

// ─── useUpdateDailyAttendance ───────────────────────────────────────
export function useUpdateDailyAttendance() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateDailyAttendanceRequest }) => {
			return dailyAttendanceService.update(id, data);
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: dailyAttendanceKeys.lists() });
			queryClient.invalidateQueries({
				queryKey: dailyAttendanceKeys.detail(variables.id),
			});
			message.success("Kunlik davomat muvaffaqiyatli yangilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Kunlik davomatni yangilashda xatolik";
			message.error(msg);
		},
	});
}

// ─── useDeleteDailyAttendance ───────────────────────────────────────
export function useDeleteDailyAttendance() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => {
			return dailyAttendanceService.delete(id);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: dailyAttendanceKeys.lists() });
			message.success("Kunlik davomat muvaffaqiyatli o'chirildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Kunlik davomatni o'chirishda xatolik";
			message.error(msg);
		},
	});
}
